import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await api.post('/auth/login', { email, password, remember_me: rememberMe });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      
      // Get user info
      const userResponse = await api.get('/auth/me');
      setUser(userResponse.data);
      
      toast.success('Connexion réussie !', {
        description: rememberMe 
          ? 'Vous resterez connecté pendant 30 jours'
          : 'Bienvenue sur Alliance Solidarité'
      });
      
      return true;
    } catch (error) {
      toast.error('Erreur de connexion', {
        description: error.response?.data?.detail || 'Email ou mot de passe incorrect'
      });
      return false;
    }
  };

  const register = async (name, email, password, competences = []) => {
    try {
      await api.post('/auth/register', { name, email, password });
      
      // Auto-login after registration
      const loginSuccess = await login(email, password);
      
      // If competences were provided, update them
      if (loginSuccess && competences.length > 0) {
        try {
          await api.put('/auth/me/competences', competences);
          toast.success('Compétences enregistrées !', {
            description: `${competences.length} catégorie(s) ajoutée(s) à votre profil`
          });
        } catch (error) {
          console.error('Error saving competences:', error);
        }
      }
      
      return loginSuccess;
    } catch (error) {
      toast.error('Erreur d\'inscription', {
        description: error.response?.data?.detail || 'Une erreur est survenue'
      });
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Déconnexion réussie');
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return true;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};